'use client';

import React from 'react';
import { ApiRequest } from '@/types/use-case';

interface ApiRequestSnippetProps {
  request: ApiRequest;
  title?: string;
}

export default function ApiRequestSnippet({ request, title = "API Request" }: ApiRequestSnippetProps) {
  // Generate URL with parameters
  const url = new URL(`https://api.tomorrow.io/v4${request.endpoint}`);
  Object.entries(request.parameters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.append(key, String(value));
    }
  });

  // Generate cURL example
  const curlCommand = `curl -X ${request.method} "${url.toString()}"${
    request.headers ? Object.entries(request.headers)
      .map(([key, value]) => ` \\\n  -H "${key}: ${value}"`)
      .join('') : ''
  }`;

  // Generate JavaScript fetch example
  const fetchExample = `const response = await fetch('${url.toString()}', {
  method: '${request.method}',${
    request.headers ? `
  headers: ${JSON.stringify(request.headers, null, 2)},` : ''
  }
});

const data = await response.json();`;

  // Generate parameters table
  const parameterEntries = Object.entries(request.parameters);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold mb-4">{title}</h3>
        
        {/* Parameters Table */}
        {parameterEntries.length > 0 && (
          <div className="mb-6">
            <h4 className="text-lg font-medium mb-3">Request Parameters</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-300 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-900 border-b">
                      Parameter
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-900 border-b">
                      Value
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-900 border-b">
                      Type
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {parameterEntries.map(([key, value], index) => (
                    <tr key={key} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-2 text-sm font-mono text-gray-900 border-b">
                        {key}
                      </td>
                      <td className="px-4 py-2 text-sm font-mono text-gray-700 border-b">
                        {typeof value === 'string' ? `"${value}"` : JSON.stringify(value)}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600 border-b">
                        {typeof value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* URL Display */}
        <div className="mb-6">
          <h4 className="text-lg font-medium mb-3">Request URL</h4>
          <div className="dark-code-block p-4 rounded-lg">
            <code style={{ fontSize: '14px', lineHeight: '1.5' }} className="break-all">
              {url.toString()}
            </code>
          </div>
        </div>

        {/* Code Examples */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h4 className="text-lg font-medium mb-3">cURL</h4>
            <div className="dark-code-block p-4 rounded-lg overflow-x-auto">
              <pre style={{ whiteSpace: 'pre-wrap' }}>
                <code style={{ fontSize: '14px', lineHeight: '1.5' }}>{curlCommand}</code>
              </pre>
            </div>
          </div>
          <div>
            <h4 className="text-lg font-medium mb-3">JavaScript</h4>
            <div className="dark-code-block p-4 rounded-lg overflow-x-auto">
              <pre style={{ whiteSpace: 'pre-wrap' }}>
                <code style={{ fontSize: '14px', lineHeight: '1.5' }}>{fetchExample}</code>
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 